import { createTeamMembersColumns } from "./-components/team-members-columns"
import { createScheduledQuizzesColumns } from "./-components/scheduled-quizzes-columns"
import { ScheduleQuizDialog } from "./-components/schedule-quiz-dialog"
import { EditScheduledQuizDialog } from "./-components/edit-scheduled-quiz-dialog"
import { DeleteScheduledQuizDialog } from "./-components/delete-scheduled-quiz-dialog"
import { InvitePlayerDialog } from "./-components/invite-player-dialog"
import { RenameTeamDialog } from "./-components/rename-team-dialog"
import { DeleteTeamDialog } from "./-components/delete-team-dialog"
import { useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { api } from "@convex/_generated/api"
import type { Id } from "@convex/_generated/dataModel"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { Users, Calendar, Plus, Pencil, Trash2 } from "lucide-react"
import { DataTable } from "@/components/ui/data-table"
import { Button } from "@/components/ui/button"

export const Route = createFileRoute("/(admin)/admin/teams/$teamId")({
  component: RouteComponent,
})

function RouteComponent() {
  const { teamId } = Route.useParams()
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteScheduledQuizDialogOpen, setDeleteScheduledQuizDialogOpen] =
    useState(false)
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [deleteTeamDialogOpen, setDeleteTeamDialogOpen] = useState(false)
  const [selectedScheduledQuizId, setSelectedScheduledQuizId] =
    useState<Id<"scheduledQuizzes"> | null>(null)

  const team = useQuery(api.teams.handlers.getTeam, {
    teamId: teamId as Id<"teams">,
  })
  const teamMembers = useQuery(api.teams.handlers.getTeamMembers, {
    teamId: teamId as Id<"teams">,
  })
  const scheduledQuizzes = useQuery(
    api.scheduledQuizzes.handlers.getScheduledQuizzesForTeam,
    { teamId: teamId as Id<"teams"> }
  )

  const handleEditScheduledQuiz = (scheduledQuizId: Id<"scheduledQuizzes">) => {
    setSelectedScheduledQuizId(scheduledQuizId)
    setEditDialogOpen(true)
  }

  const handleDeleteScheduledQuiz = (
    scheduledQuizId: Id<"scheduledQuizzes">
  ) => {
    setSelectedScheduledQuizId(scheduledQuizId)
    setDeleteScheduledQuizDialogOpen(true)
  }

  if (!team || !teamMembers || scheduledQuizzes === undefined) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4">
        <Skeleton className="h-10 w-64 mb-6" />
        <Skeleton className="h-[400px]" />
      </div>
    )
  }

  const teamMembersColumns = createTeamMembersColumns(
    teamId as Id<"teams">,
    team.captainId
  )
  const scheduledQuizzesColumns = createScheduledQuizzesColumns({
    onEdit: handleEditScheduledQuiz,
    onDelete: handleDeleteScheduledQuiz,
  })

  return (
    <div className="flex flex-1 flex-col gap-6 p-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold">{team.name}</h1>
            <Badge variant="outline" className="capitalize">
              {team.type}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            <Link to="/admin/teams" className="text-primary hover:underline">
              ← Back to Teams
            </Link>
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setRenameDialogOpen(true)
            }}
          >
            <Pencil className="mr-2 h-4 w-4" />
            Rename
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => {
              setDeleteTeamDialogOpen(true)
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      {/* Team Members Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Team Members ({teamMembers.length})
            </CardTitle>
            <Button
              size="sm"
              onClick={() => {
                setInviteDialogOpen(true)
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Invite Player
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={teamMembersColumns}
            data={teamMembers}
            filterColumn="name"
            filterPlaceholder="Search members..."
          />
        </CardContent>
      </Card>

      {/* Scheduled Quizzes Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Scheduled Quizzes ({scheduledQuizzes.length})
            </CardTitle>
            <Button
              size="sm"
              onClick={() => {
                setScheduleDialogOpen(true)
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Schedule Quiz
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={scheduledQuizzesColumns}
            data={scheduledQuizzes}
            filterColumn="quiz.name"
            filterPlaceholder="Search quizzes..."
          />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <ScheduleQuizDialog
        teamId={teamId as Id<"teams">}
        open={scheduleDialogOpen}
        onOpenChange={setScheduleDialogOpen}
      />

      <InvitePlayerDialog
        teamId={teamId as Id<"teams">}
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
      />

      <EditScheduledQuizDialog
        scheduledQuizId={selectedScheduledQuizId}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
      />

      <DeleteScheduledQuizDialog
        scheduledQuizId={selectedScheduledQuizId}
        open={deleteScheduledQuizDialogOpen}
        onOpenChange={setDeleteScheduledQuizDialogOpen}
      />

      <RenameTeamDialog
        teamId={teamId as Id<"teams">}
        currentName={team.name}
        open={renameDialogOpen}
        onOpenChange={setRenameDialogOpen}
      />

      <DeleteTeamDialog
        teamId={teamId as Id<"teams">}
        teamName={team.name}
        open={deleteTeamDialogOpen}
        onOpenChange={setDeleteTeamDialogOpen}
      />
    </div>
  )
}
